import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  Paper,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Image,
  Save,
  Business,
  Description,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const SystemSettings = () => {
  const [config, setConfig] = useState({
    platformName: 'Control Regulatorio',
    companyName: 'Xentury Global',
    logoPath: null,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConfig({
          platformName: data.platformName || 'Control Regulatorio',
          companyName: data.companyName || 'Xentury Global',
          logoPath: data.logoPath || null,
        });

        if (data.logoPath) {
          setLogoPreview(`/uploads/${data.logoPath}`);
        }
      } else {
        console.error('Error loading config');
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo es muy grande. Máximo 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Por favor selecciona un archivo de imagen');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('platformName', config.platformName);
      formData.append('companyName', config.companyName);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await fetch(`/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        await response.json();
        setSuccess('Configuración actualizada correctamente');
        setLogoFile(null);
        loadConfig();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Alert
              severity={error ? 'error' : 'success'}
              onClose={() => error ? setError('') : setSuccess('')}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              {error || success}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <Grid container spacing={3}>
        {/* Logo del Sistema */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3b82f6',
                  }}
                >
                  <Image />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Logo del Sistema
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Logo que aparece en el login
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Preview del Logo */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: '12px',
                  mb: 2,
                  p: 3,
                }}
              >
                {logoPreview ? (
                  <Box
                    component="img"
                    src={logoPreview}
                    alt="Logo Preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 180,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Image sx={{ fontSize: 80, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No hay logo configurado
                    </Typography>
                  </Box>
                )}
              </Box>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoChange}
              />
              <label htmlFor="logo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Seleccionar Logo
                </Button>
              </label>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2, textAlign: 'center' }}
              >
                Formatos: PNG, JPG, SVG • Tamaño máximo: 5MB
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración General */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                    color: '#8b5cf6',
                  }}
                >
                  <Business />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Información del Sistema
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Personaliza el nombre y descripción
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Nombre de la Plataforma */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, color: '#475569' }}
                  >
                    Nombre de la Plataforma
                  </Typography>
                  <TextField
                    fullWidth
                    value={config.platformName}
                    onChange={(e) =>
                      setConfig({ ...config, platformName: e.target.value })
                    }
                    placeholder="Nombre de tu plataforma"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />
                </Box>

                {/* Nombre de la Compañía */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, color: '#475569' }}
                  >
                    Nombre de la Compañía
                  </Typography>
                  <TextField
                    fullWidth
                    value={config.companyName}
                    onChange={(e) =>
                      setConfig({ ...config, companyName: e.target.value })
                    }
                    placeholder="Nombre de tu compañía"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />
                </Box>

                {/* Botón para guardar todo */}
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Save />}
                  onClick={handleSaveAll}
                  disabled={loading}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    mt: 2,
                  }}
                >
                  {loading ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Información Adicional */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              borderRadius: '16px',
              bgcolor: 'rgba(59, 130, 246, 0.05)',
              border: '1px solid rgba(59, 130, 246, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Description sx={{ color: '#3b82f6', mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Sobre la Configuración del Sistema
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Los cambios realizados aquí se reflejarán inmediatamente en todo el sistema.
                  El logo aparecerá en la página de login y el nombre de la plataforma se mostrará en
                  el menú lateral. Asegúrate de usar imágenes de alta calidad para el logo.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemSettings;
